import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Edit2, Plus, Trash2 } from "lucide-react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Header } from "../components/layout/Header";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL || "http://localhost:8000"}/api`;

const initialForm = {
  name: "",
  email: "",
  password: "",
  role: "kasir",
  merchant_id: "",
};

export default function Users() {
  const [users, setUsers] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState(initialForm);

  const sortedUsers = useMemo(
    () => [...users].sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""))),
    [users]
  );

  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersRes, merchantsRes] = await Promise.all([
        axios.get(`${API}/users`, { withCredentials: true }),
        axios.get(`${API}/merchants`, { withCredentials: true }),
      ]);
      setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
      setMerchants(Array.isArray(merchantsRes.data) ? merchantsRes.data : []);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      alert(error.response?.data?.detail || "Gagal mengambil data user.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openCreate = () => {
    setEditingUser(null);
    setFormData({ ...initialForm, merchant_id: merchants[0]?.id || "" });
    setIsModalOpen(true);
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setFormData({
      name: String(user.name || ""),
      email: String(user.email || ""),
      password: "",
      role: String(user.role || "kasir"),
      merchant_id: String(user.merchant_id || ""),
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const payload = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      role: formData.role,
      merchant_id: formData.role === "admin" ? "" : formData.merchant_id,
    };
    if (!editingUser || formData.password.trim()) {
      payload.password = formData.password.trim();
    }
    if (payload.role !== "admin" && !payload.merchant_id) {
      alert("User non-admin wajib memilih merchant.");
      return;
    }
    try {
      if (editingUser) {
        await axios.put(`${API}/users/${editingUser.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/auth/register`, payload, { withCredentials: true });
      }
      setIsModalOpen(false);
      setEditingUser(null);
      setFormData(initialForm);
      fetchData();
    } catch (error) {
      console.error("Failed to save user:", error);
      alert(error.response?.data?.detail || "Gagal menyimpan user.");
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm("Hapus user ini?")) return;
    try {
      await axios.delete(`${API}/users/${userId}`, { withCredentials: true });
      fetchData();
    } catch (error) {
      console.error("Failed to delete user:", error);
      alert(error.response?.data?.detail || "Gagal menghapus user.");
    }
  };

  return (
    <DashboardLayout>
      <Header title="User Management" />
      <div className="space-y-6 p-8" data-testid="users-page">
        <section className="rounded-sm border border-white/10 bg-zinc-900/50 p-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-xl font-semibold text-white">User per Merchant</h2>
              <p className="mt-1 text-sm text-zinc-500">
                Admin bisa akses semua merchant. User kasir dan investor wajib terhubung ke satu merchant.
              </p>
            </div>
            <Button onClick={openCreate} className="rounded-sm bg-[#D4AF37] font-semibold text-black hover:bg-[#C59A27]">
              <Plus className="mr-2 h-4 w-4" />
              Tambah User
            </Button>
          </div>
        </section>

        <section className="overflow-hidden rounded-sm border border-white/10 bg-zinc-900/50">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead>Nama</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Merchant</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-8 text-center text-sm text-zinc-500">Loading...</TableCell>
                </TableRow>
              ) : sortedUsers.length > 0 ? (
                sortedUsers.map((user) => (
                  <TableRow key={user.id} className="border-white/5 hover:bg-zinc-900/50">
                    <TableCell className="py-4 font-semibold text-white">{user.name}</TableCell>
                    <TableCell className="py-4 text-zinc-300">{user.email}</TableCell>
                    <TableCell className="py-4">
                      <Badge variant="outline" className={user.role === "admin" ? "border-[#D4AF37]/30 bg-[#D4AF37]/10 text-[#F3D77A]" : user.role === "investor" ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-sky-500/30 bg-sky-500/10 text-sky-300"}>
                        {user.role}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-4 text-zinc-300">{user.role === "admin" ? "Semua Merchant" : user.merchant_name || "-"}</TableCell>
                    <TableCell className="py-4 text-right">
                      <div className="flex justify-end gap-2">
                        <button type="button" onClick={() => openEdit(user)} className="rounded-sm p-2 text-zinc-400 hover:bg-zinc-800 hover:text-white">
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button type="button" onClick={() => handleDelete(user.id)} className="rounded-sm p-2 text-zinc-400 hover:bg-red-500/10 hover:text-red-400">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="py-8 text-center text-sm text-zinc-500">Belum ada user.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </section>

        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="max-w-lg border-white/10 bg-zinc-900 text-white">
            <DialogHeader>
              <DialogTitle>{editingUser ? "Edit User" : "Tambah User"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="mt-4 space-y-4">
              <div className="space-y-2">
                <Label>Nama</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>Password {editingUser ? "(kosongkan jika tidak diubah)" : ""}</Label>
                <Input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required={!editingUser} />
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value, merchant_id: value === "admin" ? "" : formData.merchant_id || merchants[0]?.id || "" })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="kasir">Kasir</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="investor">Investor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {formData.role !== "admin" && (
                <div className="space-y-2">
                  <Label>Merchant</Label>
                  <Select value={formData.merchant_id || undefined} onValueChange={(value) => setFormData({ ...formData, merchant_id: value })}>
                    <SelectTrigger><SelectValue placeholder="Pilih merchant" /></SelectTrigger>
                    <SelectContent>
                      {merchants.map((merchant) => (
                        <SelectItem key={merchant.id} value={merchant.id}>{merchant.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {merchants.length === 0 && <p className="text-xs text-amber-400">Tambahkan Merchant dulu sebelum membuat user kasir/investor.</p>}
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="flex-1 border-white/10 text-white hover:bg-zinc-800">Cancel</Button>
                <Button type="submit" className="flex-1 bg-[#D4AF37] font-semibold text-black hover:bg-[#C59A27]">{editingUser ? "Update" : "Tambah"}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
